import * as Sentry from '@sentry/react';
import { hasAnalyticsConsent } from './cookieConsent';

/**
 * Initialize Sentry error tracking
 * Only activates if VITE_SENTRY_DSN is set in environment
 * Session Replay is ONLY enabled if the user has consented to analytics cookies (GDPR/PECR)
 */
export const initSentry = () => {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development';
  
  if (!sentryDsn) {
    console.info('📊 Sentry: DSN not configured, error tracking disabled');
    return;
  }

  const analyticsConsented = hasAnalyticsConsent();

  try {
    const integrations: any[] = [
      Sentry.browserTracingIntegration(),
    ];
    
    // Only add Session Replay if user has consented to analytics cookies
    if (analyticsConsented) {
      integrations.push(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        })
      );
      console.info('📊 Sentry: Session Replay enabled (analytics consent given)');
    } else {
      console.info('📊 Sentry: Session Replay disabled (no analytics consent)');
    }

    Sentry.init({
      dsn: sentryDsn,
      environment,
      
      integrations,
      
      // Performance Monitoring sample rates
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      
      // Session Replay sample rates — only effective if replay integration is added
      replaysSessionSampleRate: analyticsConsented ? 0.1 : 0,
      replaysOnErrorSampleRate: analyticsConsented ? 1.0 : 0,
      
      // Filter out specific errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        
        // Filter out common non-actionable errors
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message).toLowerCase();
          
          // Browser extension errors
          if (message.includes('extension')) return null;
          
          // Network errors that are user-side
          if (message.includes('failed to fetch') || message.includes('network error')) {
            // Only log if it happens frequently (rate limit)
            if (Math.random() > 0.1) return null;
          }
          
          // ResizeObserver errors (cosmetic, not actionable)
          if (message.includes('resizeobserver')) return null;
        }
        
        return event;
      },
      
      // Add user context
      initialScope: {
        tags: {
          application: 'exora-club',
        },
      },
    });

    console.info('✅ Sentry initialized for error tracking');
    
    // Listen for consent changes — re-init if user later consents to analytics
    window.addEventListener('cookie-consent-changed', ((e: CustomEvent) => {
      if (e.detail?.analytics && !analyticsConsented) {
        // User just consented — add replay integration dynamically
        const client = Sentry.getClient();
        if (client) {
          client.addIntegration(
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            })
          );
          console.info('📊 Sentry: Session Replay enabled after consent');
        }
      }
    }) as EventListener);
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
};

/**
 * Capture an exception in Sentry
 */
export const captureException = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, {
      contexts: context ? { additional: context } : undefined,
    });
  } else {
    // Fallback to console in development
    console.error('Error captured:', error, context);
  }
};

/**
 * Capture a message in Sentry
 */
export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  } else {
    console[level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'log'](message);
  }
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (user: { id: string; email?: string; username?: string } | null) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    } else {
      Sentry.setUser(null);
    }
  }
};

/**
 * Add breadcrumb for debugging context
 */
export const addBreadcrumb = (message: string, data?: Record<string, any>) => {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.addBreadcrumb({
      message,
      data,
      level: 'info',
      timestamp: Date.now() / 1000,
    });
  }
};
